import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Heart, MessageCircle, Calendar, User, RefreshCw, Camera, X, Trash2 } from 'lucide-react';
import { TeamsPhotoPost, TeamsPhotoComment } from '@fitzaa/shared';
import { WebTeamsPhotoService } from '../services/TeamsPhotoService';
import { EncryptedImageStorageService } from '../services/EncryptedImageStorageService';
import { PhotoUpload } from './PhotoUpload';
import { AzureBlobStorageService } from '../services/AzureBlobStorageService';
import { ImageCacheService } from '../services/ImageCacheService';
import { getDatabase, ref, onValue, off } from 'firebase/database';
import { databasePaths } from '../config/firebase';
import { useTheme } from '../contexts/ThemeContext';

interface ChallengeWallProps {
    challengeId: string;
    challengeTitle?: string;
    className?: string;
}

export const ChallengeWall: React.FC<ChallengeWallProps> = ({
    challengeId,
    challengeTitle = 'Challenge Photos',
    className = ''
}) => {
    const [photos, setPhotos] = useState<TeamsPhotoPost[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedPhoto, setSelectedPhoto] = useState<TeamsPhotoPost | null>(null);
    const [showPhotoModal, setShowPhotoModal] = useState(false);
    const [newComment, setNewComment] = useState('');
    const [isPostingComment, setIsPostingComment] = useState(false);
    const [isLiking, setIsLiking] = useState<string | null>(null);
    const [photoUrls, setPhotoUrls] = useState<Record<string, string>>({});
    const [photoUrlExpiries, setPhotoUrlExpiries] = useState<Record<string, number>>({});
    const [urlGenerationInProgress, setUrlGenerationInProgress] = useState<Set<string>>(new Set());
    const [failedDecryptions, setFailedDecryptions] = useState<Set<string>>(new Set());
    const [isLoadingPhotos, setIsLoadingPhotos] = useState(false);
    const urlGenerationInProgressRef = useRef<Set<string>>(new Set());
    const failedDecryptionsRef = useRef<Set<string>>(new Set());
    const isLoadingPhotosRef = useRef(false);
    
    const { theme } = useTheme();
    
    // Detect mobile screen size
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Keep refs in sync with state
    useEffect(() => {
        urlGenerationInProgressRef.current = urlGenerationInProgress;
    }, [urlGenerationInProgress]);

    useEffect(() => {
        failedDecryptionsRef.current = failedDecryptions;
    }, [failedDecryptions]);

    useEffect(() => {
        isLoadingPhotosRef.current = isLoadingPhotos;
    }, [isLoadingPhotos]);

    const teamsPhotoService = WebTeamsPhotoService.getInstance();
    const encryptedImageStorageService = EncryptedImageStorageService.getInstance();

    // Consolidated effect for loading photos - prevents race conditions
    useEffect(() => {
        console.log(`[ChallengeWall] Component mounted/updated for challengeId: ${challengeId}`);

        // Prevent multiple simultaneous loadPhotos calls
        if (isLoadingPhotosRef.current) {
            console.log('[ChallengeWall] Photo loading already in progress, skipping duplicate call');
            return;
        }

        // Only load photos if service is ready
        if (!teamsPhotoService.isReady()) {
            console.log('[ChallengeWall] Service not ready yet, waiting for authentication...');
            return;
        }

        loadPhotos();
    }, [challengeId, teamsPhotoService.isReady()]);

    // Set up real-time listener for teams photos
    useEffect(() => {
        if (!challengeId || !teamsPhotoService.isReady()) {
            return;
        }

        console.log('[ChallengeWall] Setting up real-time listener for challenge:', challengeId);

        const database = getDatabase();
        const photosRef = ref(database, databasePaths.teamsPhotos());

        // Set up listener for all photos, then filter by challengeId
        const listener = onValue(photosRef, (snapshot: any) => {
            console.log('[ChallengeWall] Real-time update received for teams photos');

            if (snapshot.exists()) {
                const allPhotos: TeamsPhotoPost[] = [];

                snapshot.forEach((childSnapshot: any) => {
                    const photoData = childSnapshot.val();
                    if (photoData.challengeId === challengeId) {
                        allPhotos.push({
                            ...photoData,
                            id: childSnapshot.key || photoData.id
                        });
                    }
                });

                // Sort by posted date (newest first)
                const sortedPhotos = allPhotos.sort((a, b) =>
                    new Date(b.postedAt).getTime() - new Date(a.postedAt).getTime()
                );

                console.log(`[ChallengeWall] Real-time update: Found ${sortedPhotos.length} photos for challenge ${challengeId}`);
                setPhotos(sortedPhotos);
                setIsLoading(false);
            } else {
                console.log('[ChallengeWall] Real-time update: No photos found');
                setPhotos([]);
                setIsLoading(false);
            }
        });

        // Cleanup listener on unmount or challengeId change
        return () => {
            console.log('[ChallengeWall] Cleaning up real-time listener for challenge:', challengeId);
            off(photosRef, 'value', listener);
        };
    }, [challengeId, teamsPhotoService.isReady()]);

    // Function to download, decrypt, and create displayable image URLs with caching
    const createDisplayableImageUrl = useCallback(async (blobName: string, photosToUse?: TeamsPhotoPost[], useThumbnail: boolean = false): Promise<string> => {
        console.log(`[ChallengeWall] ===== STARTING createDisplayableImageUrl for ${blobName} =====`);
        console.log(`[ChallengeWall] Parameters:`, { blobName, useThumbnail, photosToUseLength: photosToUse?.length });

        try {
            // Use passed photos or fall back to state
            const photosToCheck = photosToUse || photos;

            // Find the photo metadata first to determine the correct blob name
            // For thumbnail requests, we need to find the photo by the main blobName, not the thumbnail blobName
            let photoData = photosToCheck.find(p => p.blobName === blobName || p.id === blobName);

            // If not found and this is a thumbnail request, try to find by thumbnailBlobName
            if (!photoData && useThumbnail) {
                photoData = photosToCheck.find(p => (p as any).thumbnailBlobName === blobName);
                console.log(`[ChallengeWall] Found photo metadata by thumbnailBlobName for: ${blobName}`);
            }

            if (!photoData) {
                console.warn(`[ChallengeWall] Photo metadata not found for blob: ${blobName}`);
                console.warn(`[ChallengeWall] Available photos:`, photosToCheck.map(p => ({ id: p.id, blobName: p.blobName, thumbnailBlobName: (p as any).thumbnailBlobName })));
                console.warn(`[ChallengeWall] Returning placeholder due to missing photo metadata`);
                throw new Error('Photo metadata not found');
            }

            // Use the appropriate blob name based on whether we want thumbnail or full image
            const targetBlobName = useThumbnail ? (photoData as any).thumbnailBlobName || photoData.blobName : photoData.blobName;

            console.log(`[ChallengeWall] 🔍 Blob name selection for photo ${photoData.id}:`, {
                useThumbnail,
                mainBlobName: photoData.blobName,
                thumbnailBlobName: (photoData as any).thumbnailBlobName,
                selectedTargetBlobName: targetBlobName,
                isUsingFallback: useThumbnail && !(photoData as any).thumbnailBlobName
            });

            if (!targetBlobName) {
                console.warn(`[ChallengeWall] No ${useThumbnail ? 'thumbnail' : 'image'} blob name found for photo ${photoData.id}`);
                throw new Error(`${useThumbnail ? 'Thumbnail' : 'Image'} blob name not found`);
            }

            // Check cache first using the correct target blob name
            const imageCacheService = ImageCacheService.getInstance();
            const cacheKey = targetBlobName; // Use the actual blob name being processed
            const cachedImage = imageCacheService.getCachedImage(cacheKey);
            if (cachedImage) {
                console.log(`[ChallengeWall] Using cached ${useThumbnail ? 'thumbnail' : 'image'} for ${targetBlobName}`);

                // Update the photoUrls state with the cached URL
                if (photoData && photoData.id) {
                    const stateKey = useThumbnail ? `${photoData.id}_thumb` : photoData.id;
                    setPhotoUrls(prev => ({ ...prev, [stateKey]: cachedImage }));
                    console.log(`[ChallengeWall] Updated photoUrls state with cached URL for photo ${photoData.id} (${useThumbnail ? 'thumbnail' : 'full'})`, {
                        stateKey,
                        cachedImage: cachedImage.substring(0, 50) + '...',
                        useThumbnail,
                        photoId: photoData.id
                    });
                }

                return cachedImage;
            }

            console.log(`[ChallengeWall] Starting createDisplayableImageUrl for ${blobName}`);
            console.log(`[ChallengeWall] Current state:`, { photosLength: photos.length, isLoading, urlGenerationInProgress: Array.from(urlGenerationInProgress) });
            console.log(`[ChallengeWall] Using ${photosToCheck.length} photos for lookup`);

            // Check if photos are loaded - if not, return a loading placeholder
            if (photosToCheck.length === 0) {
                console.log(`[ChallengeWall] Photos not loaded yet, returning loading placeholder for ${blobName}`);
                console.log(`[ChallengeWall] Current photos state:`, { photosLength: photosToCheck.length, isLoading });
                return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIjZjNmNGY2Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5YWFhYSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkxvYWRpbmcuLi48L3RleHQ+PC9zdmc+';
            }

            console.log(`[ChallengeWall] Photos are loaded, proceeding with URL generation for ${blobName}`);

            // Check if URL generation is already in progress for this blob
            if (urlGenerationInProgressRef.current.has(blobName)) {
                console.log(`[ChallengeWall] URL generation already in progress for ${blobName}, skipping duplicate call`);
                // Return a placeholder while waiting
                return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIjZjNmNGY2Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5YWFhYSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkxvYWRpbmcuLi48L3RleHQ+PC9zdmc+';
            }

            // Check if this blob has already failed decryption
            if (failedDecryptionsRef.current.has(blobName)) {
                console.log(`[ChallengeWall] Decryption already failed for ${blobName}, returning placeholder`);
                return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIjZjNmNGY2Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5YWFhYSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkRlY3J5cHRpb24gZmFpbGVkPC90ZXh0Pjwvc3ZnPg==';
            }

            // Mark this blob as being processed
            setUrlGenerationInProgress(prev => new Set(prev).add(blobName));
            console.log(`[ChallengeWall] Marked ${blobName} as in progress`);

            // Get the secure URL from Azure Blob Storage
            console.log(`[ChallengeWall] Getting secure URL for ${blobName}...`);
            const secureUrlResult = await encryptedImageStorageService.getPhotoUrl(blobName);
            console.log(`[ChallengeWall] Got secure URL for ${blobName}:`, secureUrlResult.sasUrl);

            // IMPORTANT: We should NOT return the Azure URL directly - we need to download and decrypt it
            if (secureUrlResult.sasUrl.includes('blob.core.windows.net')) {
                console.log(`[ChallengeWall] WARNING: Got Azure blob URL, proceeding with download and decryption...`);
            }

            // Download the encrypted blob
            const response = await fetch(secureUrlResult.sasUrl);
            if (!response.ok) {
                throw new Error(`Failed to download encrypted blob: ${response.status} ${response.statusText}`);
            }

            const encryptedArrayBuffer = await response.arrayBuffer();
            console.log(`[ChallengeWall] Downloaded encrypted blob for ${blobName}, size: ${encryptedArrayBuffer.byteLength} bytes`);

            // Check if this is an encrypted image (ends with .encrypted)
            if (blobName.includes('.encrypted')) {
                console.log(`[ChallengeWall] ${blobName} is encrypted, attempting decryption...`);

                try {
                    // photoData and targetBlobName are already determined above

                    // If we're looking for a thumbnail but the target blob name is different, we need to get the secure URL for the thumbnail
                    if (useThumbnail && targetBlobName !== blobName) {
                        console.log(`[ChallengeWall] Getting secure URL for thumbnail blob: ${targetBlobName}`);
                        const thumbnailSecureUrlResult = await encryptedImageStorageService.getPhotoUrl(targetBlobName);
                        const thumbnailResponse = await fetch(thumbnailSecureUrlResult.sasUrl);
                        if (!thumbnailResponse.ok) {
                            throw new Error(`Failed to download thumbnail blob: ${thumbnailResponse.status} ${thumbnailResponse.statusText}`);
                        }
                        const thumbnailEncryptedArrayBuffer = await thumbnailResponse.arrayBuffer();
                        console.log(`[ChallengeWall] Downloaded thumbnail blob for ${targetBlobName}, size: ${thumbnailEncryptedArrayBuffer.byteLength} bytes`);

                        // Use the thumbnail data for decryption
                        const thumbnailIv = new Uint8Array(thumbnailEncryptedArrayBuffer.slice(0, 16));
                        const thumbnailEncryptedData = thumbnailEncryptedArrayBuffer.slice(16);

                        // Decrypt the thumbnail
                        const decryptedThumbnailData = await encryptedImageStorageService.decryptImage(thumbnailEncryptedData, thumbnailIv, photoData.encryptionKey!);
                        const thumbnailBlob = new Blob([decryptedThumbnailData], { type: 'image/jpeg' });
                        const thumbnailDataUrl = URL.createObjectURL(thumbnailBlob);

                        // Cache the thumbnail using the actual blob name
                        imageCacheService.setCachedImage(targetBlobName, thumbnailDataUrl);

                        // Update the photoUrls state
                        if (photoData.id) {
                            const stateKey = `${photoData.id}_thumb`;
                            setPhotoUrls(prev => ({ ...prev, [stateKey]: thumbnailDataUrl }));
                            console.log(`[ChallengeWall] Updated photoUrls state with thumbnail URL for photo ${photoData.id}`);
                        }

                        return thumbnailDataUrl;
                    }

                    let iv: Uint8Array;
                    let encryptedData: ArrayBuffer;

                    // Both mobile and web use the same format: binary data with IV prepended
                    const ivLength = 16; // AES-CBC IV length
                    iv = new Uint8Array(encryptedArrayBuffer.slice(0, ivLength));
                    encryptedData = encryptedArrayBuffer.slice(ivLength);



                    console.log(`[ChallengeWall] Found photo metadata for ${blobName}, proceeding with decryption:`, {
                        photoId: photoData.id,
                        hasEncryptionKey: !!photoData.encryptionKey,
                        encryptionKeyLength: photoData.encryptionKey?.length || 0,
                        encryptionKeyPreview: photoData.encryptionKey ? `${photoData.encryptionKey.substring(0, 20)}...` : 'undefined',
                        hasEncryptionAlgorithm: !!photoData.encryptionAlgorithm,
                        encryptionAlgorithm: photoData.encryptionAlgorithm
                    });

                    if (!photoData.encryptionKey) {
                        console.warn(`[ChallengeWall] Encryption key missing for photo ${photoData.id} (blob: ${blobName}). This image was uploaded before encryption key storage was implemented.`);
                        console.warn(`[ChallengeWall] Returning placeholder due to missing encryption key`);

                        // Try to use the original photoUrl as fallback for older photos
                        if (photoData.photoUrl && photoData.photoUrl !== '') {
                            console.log(`[ChallengeWall] Using original photoUrl as fallback for photo ${photoData.id}:`, photoData.photoUrl);
                            // If the original photoUrl is also an Azure blob URL, we need to handle it differently
                            if (photoData.photoUrl.includes('blob.core.windows.net')) {
                                // This is also an Azure blob URL, so we can't use it directly
                                throw new Error('Original photoUrl is also an Azure blob URL that cannot be accessed directly');
                            }
                            return photoData.photoUrl;
                        }

                        throw new Error('Encryption key missing - image uploaded before key storage was implemented');
                    }

                    // Decrypt the image using the EncryptedImageStorageService with the stored key
                    console.log(`[ChallengeWall] Attempting decryption for ${blobName}:`, {
                        encryptedDataSize: encryptedData.byteLength,
                        ivLength: iv.length,
                        ivPreview: Array.from(iv.slice(0, 4)).join(','),
                        encryptionKeyLength: photoData.encryptionKey?.length || 0
                    });

                    const decryptedData = await encryptedImageStorageService.decryptImage(
                        encryptedData,
                        iv,
                        photoData.encryptionKey
                    );

                    console.log(`[ChallengeWall] Decryption successful for ${blobName}:`, {
                        decryptedDataSize: decryptedData.byteLength,
                        isArrayBuffer: decryptedData instanceof ArrayBuffer
                    });

                    // Create a blob URL from the decrypted data
                    // Use the original content type from metadata or filename
                    const contentType = photoData.originalContentType ||
                        (blobName.includes('.png') ? 'image/png' :
                            blobName.includes('.heic') ? 'image/heic' : 'image/jpeg');

                    const blob = new Blob([decryptedData], { type: contentType });
                    const blobUrl = URL.createObjectURL(blob);

                    console.log(`[ChallengeWall] Successfully decrypted and created blob URL for ${blobName}:`, {
                        blobUrl,
                        isBlobUrl: blobUrl.startsWith('blob:'),
                        contentType,
                        decryptedSize: decryptedData.byteLength
                    });

                    // CRITICAL: Ensure we're returning a blob URL, not an Azure URL
                    if (!blobUrl.startsWith('blob:')) {
                        console.error(`[ChallengeWall] ERROR: Function is returning non-blob URL:`, blobUrl);
                        throw new Error('Function returned non-blob URL instead of decrypted blob URL');
                    }

                    // Cache the decrypted image using the actual blob name, including the blob object to prevent garbage collection
                    imageCacheService.setCachedImage(targetBlobName, blobUrl, blobUrl, blob);

                    // Update the photoUrls state
                    if (photoData && photoData.id) {
                        const stateKey = useThumbnail ? `${photoData.id}_thumb` : photoData.id;
                        setPhotoUrls(prev => ({ ...prev, [stateKey]: blobUrl }));
                        console.log(`[ChallengeWall] Updated photoUrls state with decrypted URL for photo ${photoData.id} (${useThumbnail ? 'thumbnail' : 'full'})`, {
                            stateKey,
                            blobUrl: blobUrl.substring(0, 50) + '...',
                            useThumbnail,
                            photoId: photoData.id
                        });
                    }

                    return blobUrl;

                } catch (decryptError) {
                    console.error(`[ChallengeWall] Failed to decrypt ${blobName}:`, decryptError);
                    console.error(`[ChallengeWall] Decryption error details:`, {
                        errorName: decryptError instanceof Error ? decryptError.name : 'Unknown',
                        errorMessage: decryptError instanceof Error ? decryptError.message : String(decryptError),
                        blobName
                    });

                    // Mark this blob as failed to prevent infinite retry loops
                    setFailedDecryptions(prev => new Set(prev).add(blobName));
                    console.log(`[ChallengeWall] Marked ${blobName} as failed decryption`);

                    // If decryption fails, return a placeholder
                    return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3ZnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIjZjNmNGY2Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5YWFhYSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkRlY3J5cHRpb24gZmFpbGVkPC90ZXh0Pjwvc3ZnPg==';
                }
            } else {
                // Not encrypted, use the data directly
                console.log(`[ChallengeWall] ${blobName} is not encrypted, using data directly`);
                const contentType = blobName.includes('.png') ? 'image/png' :
                    blobName.includes('.heic') ? 'image/heic' : 'image/jpeg';

                const blob = new Blob([encryptedArrayBuffer], { type: contentType });
                const blobUrl = URL.createObjectURL(blob);

                console.log(`[ChallengeWall] Created blob URL for non-encrypted ${blobName}:`, {
                    blobUrl,
                    isBlobUrl: blobUrl.startsWith('blob:'),
                    contentType
                });

                // CRITICAL: Ensure we're returning a blob URL, not an Azure URL
                if (!blobUrl.startsWith('blob:')) {
                    console.error(`[ChallengeWall] ERROR: Function is returning non-blob URL:`, blobUrl);
                    throw new Error('Function returned non-blob URL instead of blob URL');
                }

                // Cache the non-encrypted image using the actual blob name
                imageCacheService.setCachedImage(targetBlobName, blobUrl);

                // Update the photoUrls state
                if (photoData && photoData.id) {
                    const stateKey = useThumbnail ? `${photoData.id}_thumb` : photoData.id;
                    setPhotoUrls(prev => ({ ...prev, [stateKey]: blobUrl }));
                    console.log(`[ChallengeWall] Updated photoUrls state with non-encrypted URL for photo ${photoData.id} (${useThumbnail ? 'thumbnail' : 'full'})`);
                }

                return blobUrl;
            }

        } catch (error) {
            console.error(`[ChallengeWall] ===== ERROR in createDisplayableImageUrl for ${blobName} =====`, error);
            console.error(`[ChallengeWall] Main error details:`, {
                errorName: error instanceof Error ? error.name : 'Unknown',
                errorMessage: error instanceof Error ? error.message : String(error),
                blobName,
                stack: error instanceof Error ? error.stack : 'No stack trace'
            });

            // Return a placeholder image as fallback
            const placeholderUrl = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3ZnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIjZjNmNGY2Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5YWFhYSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkltYWdlIG5vdCBhdmFpbGFibGU8L3RleHQ+PC9zdmc+';
            console.log(`[ChallengeWall] ===== RETURNING placeholder URL for ${blobName} =====`, placeholderUrl.substring(0, 50) + '...');
            console.log(`[ChallengeWall] Placeholder return reason: Main catch block - function failed`);
            return placeholderUrl;
        } finally {
            // Always remove from in-progress set when done
            setUrlGenerationInProgress(prev => {
                const newSet = new Set(prev);
                newSet.delete(blobName);
                return newSet;
            });
            console.log(`[ChallengeWall] Removed ${blobName} from in-progress set`);
        }
    }, [encryptedImageStorageService]);

    // Function to check and refresh expired URLs
    const checkAndRefreshExpiredUrls = useCallback(async () => {
        const now = Date.now();
        const expiredPhotoIds: string[] = [];

        // Check which URLs are expired or close to expiring (within 5 minutes)
        Object.entries(photoUrlExpiries).forEach(([photoId, expiryTime]) => {
            const timeUntilExpiry = expiryTime - now;
            const minutesUntilExpiry = Math.floor(timeUntilExpiry / (1000 * 60));

            if (minutesUntilExpiry <= 5) { // Refresh if expiring within 5 minutes
                expiredPhotoIds.push(photoId);
            }
        });

        if (expiredPhotoIds.length > 0) {
            console.log(`[ChallengeWall] Refreshing ${expiredPhotoIds.length} expired/expiring URLs:`, expiredPhotoIds);

            // Get the photos that need URL refresh
            const photosToRefresh = photos.filter(photo => expiredPhotoIds.includes(photo.id || ''));

            // Generate new secure URLs for expired photos
            const urlMap: Record<string, string> = { ...photoUrls };
            const urlExpiryMap: Record<string, number> = { ...photoUrlExpiries };

            for (const photo of photosToRefresh) {
                try {
                    if (!photo.id) continue;

                    const blobName = photo.blobName || photo.id;
                    const displayableUrl = await createDisplayableImageUrl(blobName);

                    urlMap[photo.id] = displayableUrl;
                    // Set a reasonable expiry time for blob URLs (1 hour)
                    urlExpiryMap[photo.id] = Date.now() + (60 * 60 * 1000);

                    console.log(`[ChallengeWall] Refreshed URL for photo ${photo.id}`);
                } catch (error) {
                    console.error(`[ChallengeWall] Failed to refresh URL for photo ${photo.id}:`, error);
                }
            }

            setPhotoUrls(urlMap);
            setPhotoUrlExpiries(urlExpiryMap);
        }
    }, [photoUrlExpiries, photos, photoUrls, encryptedImageStorageService, createDisplayableImageUrl]);

    // Check for expired URLs every 2 minutes
    useEffect(() => {
        const interval = setInterval(() => {
            checkAndRefreshExpiredUrls();
        }, 2 * 60 * 1000); // 2 minutes

        return () => clearInterval(interval);
    }, [checkAndRefreshExpiredUrls]); // Only depend on the memoized function



    // Cleanup blob URLs when component unmounts
    useEffect(() => {
        return () => {
            // Clean up all blob URLs to prevent memory leaks
            Object.values(photoUrls).forEach(url => {
                if (url.startsWith('blob:')) {
                    URL.revokeObjectURL(url);
                }
            });
        };
    }, [photoUrls]);

    // Debug: Log when photoUrls state changes
    useEffect(() => {
        console.log(`[ChallengeWall] photoUrls state updated:`, photoUrls);
        console.log(`[ChallengeWall] photoUrls keys:`, Object.keys(photoUrls));
        console.log(`[ChallengeWall] photoUrls values:`, Object.values(photoUrls).map(url => url.substring(0, 50) + '...'));
    }, [photoUrls]);

    // Generate full image URL when modal opens
    useEffect(() => {
        if (showPhotoModal && selectedPhoto && selectedPhoto.id && selectedPhoto.blobName && !photoUrls[selectedPhoto.id]) {
            console.log(`[ChallengeWall] Modal opened, triggering full image load for photo ${selectedPhoto.id}`);
            createDisplayableImageUrl(selectedPhoto.blobName, [selectedPhoto], false);
        }
    }, [showPhotoModal, selectedPhoto, photoUrls, createDisplayableImageUrl]);

    // Generate URLs for new photos when photos state changes
    useEffect(() => {
        if (photos.length > 0) {
            console.log(`[ChallengeWall] Photos state changed, generating URLs for ${photos.length} photos`);

            // Generate URLs for all photos that don't have them yet
            photos.forEach(photo => {
                // Debug: Log the complete photo structure
                console.log(`[ChallengeWall] 🔍 Photo ${photo.id} structure:`, {
                    id: photo.id,
                    blobName: photo.blobName,
                    thumbnailBlobName: (photo as any).thumbnailBlobName,
                    thumbnailUrl: (photo as any).thumbnailUrl,
                    photoUrl: photo.photoUrl,
                    allKeys: Object.keys(photo)
                });
                // Skip if this photo's blob has already failed decryption
                if (photo.blobName && failedDecryptionsRef.current.has(photo.blobName)) {
                    console.log(`[ChallengeWall] Skipping ${photo.id} - decryption already failed for blob: ${photo.blobName}`);
                    return;
                }

                // Skip if URL generation is already in progress for this blob
                const thumbnailBlobName = (photo as any).thumbnailBlobName;
                if (photo.blobName && urlGenerationInProgressRef.current.has(photo.blobName)) {
                    console.log(`[ChallengeWall] Skipping ${photo.id} - URL generation already in progress for blob: ${photo.blobName}`);
                    return;
                }
                if (thumbnailBlobName && urlGenerationInProgressRef.current.has(thumbnailBlobName)) {
                    console.log(`[ChallengeWall] Skipping ${photo.id} - URL generation already in progress for thumbnail blob: ${thumbnailBlobName}`);
                    return;
                }

                // ONLY generate thumbnail URLs for grid display - never full images
                if (photo.blobName && photo.id && !photoUrls[`${photo.id}_thumb`]) {
                    const thumbnailBlobName = (photo as any).thumbnailBlobName;

                    if (!thumbnailBlobName) {
                        console.log(`[ChallengeWall] ⚠️ No thumbnail available for photo ${photo.id} - this photo cannot be displayed in grid view`);
                        console.log(`[ChallengeWall] ⚠️ Photo ${photo.id} will show as unavailable until thumbnail is generated`);
                        // Don't generate any URL for photos without thumbnails
                        return;
                    }

                    console.log(`[ChallengeWall] 🔍 Generating THUMBNAIL ONLY for grid display: ${photo.id}`, {
                        blobName: photo.blobName,
                        thumbnailBlobName: thumbnailBlobName,
                        willGenerateThumbnail: true,
                        photoId: photo.id
                    });

                    // Generate ONLY thumbnail URL for grid display
                    const photoWithThumbnail = { ...photo, thumbnailBlobName };
                    console.log(`[ChallengeWall] Calling createDisplayableImageUrl with:`, {
                        thumbnailBlobName,
                        useThumbnail: true,
                        photoId: photo.id
                    });
                    createDisplayableImageUrl(thumbnailBlobName, [photoWithThumbnail], true);
                } else if (photo.blobName && photo.id) {
                    console.log(`[ChallengeWall] ⏭️ Thumbnail already exists for photo ${photo.id}:`, {
                        hasThumbnailUrl: !!photoUrls[`${photo.id}_thumb`],
                        thumbnailUrl: photoUrls[`${photo.id}_thumb`] ? photoUrls[`${photo.id}_thumb`].substring(0, 50) + '...' : null
                    });
                }

                // NEVER generate full image URLs automatically - only when modal is opened
                // This saves bandwidth and improves performance significantly
            });
        }
    }, [photos, createDisplayableImageUrl]);

    const loadPhotos = async () => {
        console.log(`[ChallengeWall] loadPhotos called for challengeId: ${challengeId}`);

        // Prevent multiple simultaneous calls
        if (isLoadingPhotosRef.current) {
            console.log('[ChallengeWall] Photo loading already in progress, skipping duplicate call');
            return;
        }

        try {
            // Check if the service is ready
            if (!teamsPhotoService.isReady()) {
                console.log('[ChallengeWall] TeamsPhotoService not ready yet, waiting for authentication...');
                setError('Service not ready. Please wait for authentication to complete.');
                return; // Don't proceed if service isn't ready
            }

            console.log('[ChallengeWall] Service is ready, starting to load photos...');
            setIsLoadingPhotos(true);
            setIsLoading(true);
            setError(null);
            const teamsPhotoFirebaseService = teamsPhotoService.getTeamsPhotoFirebaseService();
            const challengePhotos = await teamsPhotoFirebaseService.getChallengePhotos(challengeId);

            // Ensure all photos have default values for missing fields
            const safePhotos = challengePhotos.map(photo => ({
                ...photo,
                comments: photo.comments || [],
                likes: photo.likes || 0,
                caption: photo.caption || undefined,
                teamsMessageId: photo.teamsMessageId || `azure-${Date.now()}`,
                teamsChannelId: photo.teamsChannelId || `challenge-${challengeId}`,
                ivLength: photo.ivLength?.toString(),
                originalSize: photo.originalSize?.toString(),
                optimizedSize: photo.optimizedSize?.toString(),
                compressionRatio: photo.compressionRatio?.toString()
            }));

            setPhotos(safePhotos);
            console.log(`[ChallengeWall] Loaded ${safePhotos.length} photos successfully`);
            console.log(`[ChallengeWall] Photos loaded:`, safePhotos.map(p => ({ id: p.id, blobName: p.blobName, hasEncryptionKey: !!p.encryptionKey })));

            // Generate displayable URLs for all photos - pass the photos directly to avoid race condition
            await generateSecureUrls(safePhotos);
        } catch (error: any) {
            console.error('[ChallengeWall] Error loading photos:', error);
            setError(error.message || 'Failed to load photos');
        } finally {
            setIsLoadingPhotos(false);
            setIsLoading(false);
        }
    };

    const generateSecureUrls = useCallback(async (photosToProcess: TeamsPhotoPost[]) => {
        try {
            console.log(`[ChallengeWall] generateSecureUrls called with ${photosToProcess.length} photos`);
            const urlMap: Record<string, string> = {};
            const urlExpiryMap: Record<string, number> = {}; // Store expiration times

            for (const photo of photosToProcess) {
                try {
                    // Skip photos without an ID
                    if (!photo.id) {
                        console.warn(`[ChallengeWall] Photo missing ID, skipping:`, photo);
                        continue;
                    }

                    // SKIP main photo URL generation - we only want thumbnails for grid display
                    console.log(`[ChallengeWall] Skipping main photo URL generation for photo ${photo.id} - using thumbnail-only policy`);

                    // Don't generate main photo URLs - only thumbnails are generated in the useEffect
                    // This prevents overwriting the thumbnail URL with the main photo URL
                } catch (error) {
                    console.error(`[ChallengeWall] Failed to get displayable URL for photo ${photo.id}:`, error);

                    // Try to use the original photoUrl as fallback for older photos
                    if (photo.photoUrl && photo.photoUrl !== '' && photo.id) {
                        console.log(`[ChallengeWall] Using original photoUrl as fallback for photo ${photo.id}:`, photo.photoUrl);
                        urlMap[photo.id] = photo.photoUrl;
                        // Set a shorter expiry for fallback URLs (30 minutes)
                        urlExpiryMap[photo.id] = Date.now() + (30 * 60 * 1000);
                        continue;
                    }

                    // If no fallback available, log the error and skip this photo
                    if (photo.id) {
                        console.warn(`[ChallengeWall] Skipping photo ${photo.id} due to displayable URL generation failure and no fallback available`);
                    }
                }
            }

            setPhotoUrls(urlMap);
            setPhotoUrlExpiries(urlExpiryMap); // Store expiration times for later use

            // Debug: Log what URLs were set
            console.log(`[ChallengeWall] Set ${Object.keys(urlMap).length} displayable URLs:`, urlMap);
            console.log(`[ChallengeWall] Set ${Object.keys(urlExpiryMap).length} expiration times:`, urlExpiryMap);
        } catch (error) {
            console.error('[ChallengeWall] Error generating displayable URLs:', error);
        }
    }, [createDisplayableImageUrl]);

    const handlePhotoUploaded = async (photoId: string) => {
        // Reload photos to show the new one, but only if not already loading
        if (!isLoadingPhotosRef.current) {
            await loadPhotos();
        } else {
            console.log('[ChallengeWall] Photo uploaded but already loading photos, skipping reload');
        }
    };

    const handlePhotoClick = (photo: TeamsPhotoPost) => {
        setSelectedPhoto(photo);
        setShowPhotoModal(true);
    };

    const handleCloseModal = () => {
        setShowPhotoModal(false);
        setSelectedPhoto(null);
        setNewComment('');
    };

    const handleLike = async (photoId: string) => {
        if (isLiking || !photoId) return;

        try {
            if (!teamsPhotoService.isReady()) {
                console.log('[ChallengeWall] TeamsPhotoService not ready yet, cannot like photo');
                return;
            }

            setIsLiking(photoId);
            const photo = photos.find(p => p.id === photoId);
            if (photo) {
                const newLikeCount = photo.likes + 1;
                const teamsPhotoFirebaseService = teamsPhotoService.getTeamsPhotoFirebaseService();
                await teamsPhotoFirebaseService.updatePhotoLikes(photoId, newLikeCount);

                // Update local state
                setPhotos(prev => prev.map(p =>
                    p.id === photoId ? { ...p, likes: newLikeCount } : p
                ));
            }
        } catch (error: any) {
            console.error('Error liking photo:', error);
        } finally {
            setIsLiking(null);
        }
    };

    const handleAddComment = async () => {
        if (!selectedPhoto || !newComment.trim() || isPostingComment) return;

        try {
            if (!teamsPhotoService.isReady()) {
                console.log('[ChallengeWall] TeamsPhotoService not ready yet, cannot add comment');
                return;
            }

            setIsPostingComment(true);
            const comment: TeamsPhotoComment = {
                id: `comment-${Date.now()}`,
                userId: 'current-user', // This should come from auth context
                userDisplayName: 'You', // This should come from auth context
                comment: newComment.trim(),
                postedAt: new Date().toISOString()
            };

            if (!selectedPhoto.id) {
                console.error('Cannot add comment: photo ID is undefined');
                return;
            }

            const teamsPhotoFirebaseService = teamsPhotoService.getTeamsPhotoFirebaseService();
            await teamsPhotoFirebaseService.addCommentToPhoto(selectedPhoto.id, comment);

            // Update local state
            setPhotos(prev => prev.map(p =>
                p.id === selectedPhoto.id
                    ? { ...p, comments: [...(p.comments || []), comment] }
                    : p
            ));

            // Update selected photo
            setSelectedPhoto(prev => prev ? {
                ...prev,
                comments: [...(prev.comments || []), comment]
            } : null);

            setNewComment('');
        } catch (error: any) {
            console.error('Error adding comment:', error);
        } finally {
            setIsPostingComment(false);
        }
    };



    const handleDeletePhoto = async (photo: TeamsPhotoPost) => {
        try {
            if (!teamsPhotoService.isReady()) {
                console.log('[ChallengeWall] TeamsPhotoService not ready yet, cannot delete photo');
                return;
            }

            if (!photo.id || typeof photo.id !== 'string' || photo.id.trim() === '') {
                console.error('Cannot delete photo: photo ID is invalid:', photo.id);
                alert('Error: Cannot delete photo: invalid photo ID');
                return;
            }

            console.log('[ChallengeWall] Deleting photo:', photo.id);
            console.log('[ChallengeWall] Photo object:', {
                id: photo.id,
                idType: typeof photo.id,
                challengeId: photo.challengeId,
                userDisplayName: photo.userDisplayName,
                hasBlobName: !!photo.blobName
            });

            // Delete both full image and thumbnail from Azure Blob Storage
            const azureBlobService = AzureBlobStorageService.getInstance();

            // Debug: Log the photo object structure
            console.log('[ChallengeWall] 🔍 Photo object for deletion:', {
                id: photo.id,
                blobName: photo.blobName,
                thumbnailBlobName: (photo as any).thumbnailBlobName,
                hasThumbnailBlobName: !!(photo as any).thumbnailBlobName,
                thumbnailUrl: (photo as any).thumbnailUrl,
                allKeys: Object.keys(photo),
                fullPhotoObject: photo
            });

            const deletionPromises = [];

            // Delete full-size image if blobName exists
            if (photo.blobName) {
                deletionPromises.push(
                    azureBlobService.deletePhoto(photo.blobName)
                        .then(() => {
                            console.log('[ChallengeWall] Full image deleted from Azure Blob Storage:', photo.blobName);
                        })
                        .catch((error) => {
                            console.error('[ChallengeWall] Failed to delete full image from Azure Blob Storage:', error);
                            throw new Error(`Failed to delete full image: ${error.message}`);
                        })
                );
            }

            // Delete thumbnail if thumbnailBlobName exists
            let thumbnailBlobName = (photo as any).thumbnailBlobName;

            // Fallback: if thumbnailBlobName is missing, we can't reliably construct it
            // because thumbnails and full images have different timestamp prefixes
            if (!thumbnailBlobName && photo.blobName) {
                console.log(`[ChallengeWall] ⚠️ Cannot construct thumbnail blob name for deletion - thumbnails and full images have different timestamps`);
                console.log(`[ChallengeWall] ⚠️ Skipping thumbnail deletion for photo ${photo.id}`);
            }

            if (thumbnailBlobName) {
                deletionPromises.push(
                    azureBlobService.deletePhoto(thumbnailBlobName)
                        .then(() => {
                            console.log('[ChallengeWall] ✅ Thumbnail deleted from Azure Blob Storage:', thumbnailBlobName);
                        })
                        .catch((error) => {
                            console.error('[ChallengeWall] ❌ Failed to delete thumbnail from Azure Blob Storage:', error);
                            throw new Error(`Failed to delete thumbnail: ${error.message}`);
                        })
                );
            } else {
                console.warn('[ChallengeWall] ⚠️ No thumbnail blob name found for photo');
            }

            // Wait for all deletions to complete
            if (deletionPromises.length > 0) {
                console.log(`[ChallengeWall] 🗑️ Starting deletion of ${deletionPromises.length} blob(s) from Azure Blob Storage`);
                try {
                    await Promise.all(deletionPromises);
                    console.log('[ChallengeWall] ✅ All images deleted from Azure Blob Storage successfully');
                } catch (azureError) {
                    console.error('[ChallengeWall] ❌ Failed to delete images from Azure Blob Storage:', azureError);
                    const errorMessage = azureError instanceof Error ? azureError.message : 'Unknown error';
                    alert(`Failed to delete images from cloud storage: ${errorMessage}. Photo was not deleted.`);
                    return; // Don't proceed with Firebase deletion if cloud storage deletion fails
                }
            } else {
                console.warn('[ChallengeWall] ⚠️ No blob names found for photo, skipping Azure deletion');
            }

            // Only delete from Firebase Database after successful cloud storage deletion
            const teamsPhotoFirebaseService = teamsPhotoService.getTeamsPhotoFirebaseService();
            await teamsPhotoFirebaseService.deletePhoto(photo.id);
            console.log('[ChallengeWall] Photo deleted from Firebase Database');

            // Clean up any blob URLs in memory (both full image and thumbnail)
            if (photo.id) {
                // Clean up full image URL
                if (photoUrls[photo.id]) {
                    const url = photoUrls[photo.id];
                    if (url.startsWith('blob:')) {
                        URL.revokeObjectURL(url);
                        console.log('[ChallengeWall] Cleaned up full image blob URL for deleted photo');
                    }
                }

                // Clean up thumbnail URL
                const thumbnailKey = `${photo.id}_thumb`;
                if (photoUrls[thumbnailKey]) {
                    const thumbnailUrl = photoUrls[thumbnailKey];
                    if (thumbnailUrl.startsWith('blob:')) {
                        URL.revokeObjectURL(thumbnailUrl);
                        console.log('[ChallengeWall] Cleaned up thumbnail blob URL for deleted photo');
                    }
                }
            }

            // Reload photos to remove the deleted one from the grid, but only if not already loading
            if (!isLoadingPhotosRef.current) {
                await loadPhotos();
            } else {
                console.log('[ChallengeWall] Photo deleted but already loading photos, skipping reload');
            }
            setSelectedPhoto(null); // Close modal if the deleted photo was selected
            setShowPhotoModal(false);

            console.log('[ChallengeWall] Photo deletion completed successfully');
            alert('Photo deleted successfully!');
        } catch (error: any) {
            alert('Failed to delete photo: ' + error.message);
            console.error('Error deleting photo:', error);
        }
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));

        if (diffInHours < 1) return 'Just now';
        if (diffInHours < 24) return `${diffInHours}h ago`;
        if (diffInHours < 48) return 'Yesterday';
        return date.toLocaleDateString();
    };

    if (isLoading) {
        return (
            <div className={`flex items-center justify-center p-8 ${className}`}>
                <div className="flex items-center space-x-2">
                    <RefreshCw className="w-5 h-5 animate-spin text-blue-600" />
                    <span className="text-gray-600 dark:text-gray-400">Loading photos...</span>
                </div>
            </div>
        );
    }

    return (
        <div className={`space-y-4 ${className}`} style={{ padding: isMobile ? '0' : undefined }}>
            {/* Simplified Header */}
            <div className="flex items-center justify-between" style={{ marginBottom: isMobile ? '12px' : undefined }}>
                <h2 style={{
                    fontSize: theme.typography.h3.fontSize,
                    fontWeight: theme.typography.h3.fontWeight,
                    color: theme.colors.text
                }}>
                    {challengeTitle}
                </h2>
                <button
                    onClick={() => {
                        if (isLoadingPhotosRef.current) {
                            console.log('[ChallengeWall] Manual refresh requested but already loading, skipping');
                            return;
                        }
                        console.log('[ChallengeWall] Manual refresh requested');
                        loadPhotos();
                    }}
                    disabled={isLoadingPhotos}
                    className={`flex items-center transition-colors duration-200 rounded-lg ${isLoadingPhotos
                        ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed'
                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800'
                        }`}
                    style={{
                        padding: isMobile ? '6px 10px' : '12px 16px',
                        fontSize: isMobile ? '12px' : '14px'
                    }}
                >
                    <RefreshCw className={`${isLoadingPhotos ? 'animate-spin' : ''}`} style={{ width: isMobile ? '14px' : '16px', height: isMobile ? '14px' : '16px', marginRight: isMobile ? '4px' : '6px' }} />
                    {isLoadingPhotos ? 'Loading...' : 'Refresh'}
                </button>
            </div>



            {/* Photo Upload */}
            <PhotoUpload
                challengeId={challengeId}
                onPhotoUploaded={handlePhotoUploaded}
            />

            {/* Error Message */}
            {error && (
                <div className="p-4 bg-red-100 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400">
                    {error}
                </div>
            )}

            {/* Photos Grid */}
            {photos.length === 0 ? (
                <div className="text-center" style={{ padding: isMobile ? '24px 12px' : '48px 24px' }}>
                    <div style={{
                        width: isMobile ? '48px' : '64px',
                        height: isMobile ? '48px' : '64px',
                        margin: '0 auto 16px',
                        backgroundColor: 'rgba(156, 163, 175, 0.1)',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <Camera style={{ width: isMobile ? '24px' : '32px', height: isMobile ? '24px' : '32px', color: '#9ca3af' }} />
                    </div>
                    <h3 style={{
                        fontSize: isMobile ? '14px' : '18px',
                        fontWeight: '500',
                        color: 'inherit',
                        marginBottom: isMobile ? '8px' : '8px'
                    }}>
                        No photos yet
                    </h3>
                    <p style={{
                        fontSize: isMobile ? '12px' : '14px',
                        color: 'inherit',
                        opacity: 0.7
                    }}>
                        Be the first to share a photo from this challenge!
                    </p>
                </div>
            ) : (
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fill, minmax(200px, 1fr))',
                    gap: isMobile ? '8px' : '16px'
                }}>
                    {photos.map((photo) => (
                        <div
                            key={photo.id || 'temp-id'}
                            className="bg-white dark:bg-gray-800 rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700 hover:shadow-xl hover:scale-105 transition-all duration-300 cursor-pointer group"
                            onClick={() => handlePhotoClick(photo)}
                            style={{
                                borderRadius: isMobile ? '10px' : '12px'
                            }}
                        >
                            {/* Photo */}
                            <div className="relative">

                                <img
                                    key={photo.id}
                                    src={photo.id && photoUrls[`${photo.id}_thumb`] ? photoUrls[`${photo.id}_thumb`] : 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIjZjNmNGY2Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5YWFhYSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkxvYWRpbmcuLi48L3RleHQ+PC9zdmc+'}
                                    data-debug={`Photo ${photo.id}: ${photo.id && photoUrls[`${photo.id}_thumb`] ? 'Using thumbnail URL' : 'Using placeholder (thumbnail-only policy)'}`}
                                    alt={photo.caption || 'Challenge photo'}
                                    className="w-full object-cover bg-gray-100 dark:bg-gray-700 group-hover:scale-110 transition-transform duration-300"
                                    style={{
                                        display: 'block',
                                        maxWidth: '100%',
                                        height: isMobile ? '140px' : '208px',
                                        objectFit: 'cover'
                                    }}
                                />
                                {/* Show overlay only for photos that actually have thumbnails but failed to load - REMOVED FOR NOW */}
                                {false && photo.id && (photo as any).thumbnailBlobName && !photoUrls[`${photo.id}_thumb`] && (
                                    <div className="absolute inset-0 bg-gray-900/50 flex flex-col items-center justify-center p-4">
                                        <div className="text-white text-center mb-4">
                                            <div className="text-lg font-medium mb-2">Thumbnail Loading...</div>
                                            <div className="text-sm opacity-80">
                                                Generating thumbnail from encrypted image...
                                            </div>
                                        </div>
                                        <div className="flex space-x-2">
                                            <button
                                                onClick={async () => {
                                                    if (photo.id && (photo as any).thumbnailBlobName) {
                                                        try {
                                                            console.log(`[ChallengeWall] Retrying thumbnail load for photo ${photo.id}`);
                                                            const displayableUrl = await createDisplayableImageUrl((photo as any).thumbnailBlobName, [photo], true);
                                                            setPhotoUrls(prev => ({
                                                                ...prev,
                                                                [`${photo.id}_thumb`]: displayableUrl
                                                            }));
                                                        } catch (error) {
                                                            console.error(`[ChallengeWall] Thumbnail retry failed for photo ${photo.id}:`, error);
                                                        }
                                                    }
                                                }}
                                                className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200"
                                            >
                                                🔄 Retry
                                            </button>
                                        </div>
                                    </div>
                                )}
                                <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                                    <div className="flex space-x-2">
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (window.confirm('Are you sure you want to delete this photo?')) {
                                                    handleDeletePhoto(photo);
                                                }
                                            }}
                                            className="p-2 bg-red-500/80 hover:bg-red-600 text-white rounded-full transition-all duration-200 hover:scale-110"
                                            title="Delete photo"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Content */}
                            <div style={{ padding: isMobile ? '8px' : '12px' }}>
                                {photo.caption && (
                                    <p style={{
                                        color: 'inherit',
                                        marginBottom: isMobile ? '6px' : '12px',
                                        fontSize: isMobile ? '11px' : '14px',
                                        display: '-webkit-box',
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: 'vertical',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        lineHeight: '1.4'
                                    }}>
                                        {photo.caption}
                                    </p>
                                )}

                                {/* User Info */}
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    marginBottom: isMobile ? '6px' : '8px'
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '6px' : '8px' }}>
                                        <div style={{
                                            width: isMobile ? '24px' : '28px',
                                            height: isMobile ? '24px' : '28px',
                                            background: 'linear-gradient(135deg, #3b82f6 0%, #9333ea 100%)',
                                            borderRadius: '50%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}>
                                            <User style={{ width: isMobile ? '12px' : '14px', height: isMobile ? '12px' : '14px', color: '#ffffff' }} />
                                        </div>
                                        <span style={{
                                            fontSize: isMobile ? '11px' : '14px',
                                            fontWeight: '500',
                                            color: 'inherit'
                                        }}>
                                            {photo.userDisplayName}
                                        </span>
                                    </div>
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        fontSize: isMobile ? '9px' : '12px',
                                        color: 'inherit',
                                        opacity: 0.7
                                    }}>
                                        <Calendar style={{ width: isMobile ? '10px' : '12px', height: isMobile ? '10px' : '12px', marginRight: '4px' }} />
                                        {formatDate(photo.postedAt)}
                                    </div>
                                </div>

                                {/* Stats */}
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    fontSize: isMobile ? '11px' : '14px',
                                    color: 'inherit',
                                    opacity: 0.7
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '12px' : '16px' }}>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (photo.id) {
                                                    handleLike(photo.id);
                                                }
                                            }}
                                            disabled={isLiking === photo.id}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '4px',
                                                cursor: isLiking === photo.id ? 'not-allowed' : 'pointer',
                                                opacity: isLiking === photo.id ? 0.5 : 1,
                                                background: 'transparent',
                                                border: 'none',
                                                padding: 0
                                            }}
                                        >
                                            <Heart style={{
                                                width: isMobile ? '12px' : '16px',
                                                height: isMobile ? '12px' : '16px',
                                                color: photo.likes > 0 ? '#ef4444' : 'inherit',
                                                fill: photo.likes > 0 ? '#ef4444' : 'none'
                                            }} />
                                            <span style={{ fontSize: isMobile ? '10px' : '12px' }}>{photo.likes}</span>
                                        </button>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                            <MessageCircle style={{ width: isMobile ? '12px' : '16px', height: isMobile ? '12px' : '16px' }} />
                                            <span style={{ fontSize: isMobile ? '10px' : '12px' }}>{photo.comments?.length || 0}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Photo Modal */}
            {showPhotoModal && selectedPhoto && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6">
                            <div className="flex items-start justify-between mb-4">
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                    Photo Details
                                </h3>
                                <button
                                    onClick={handleCloseModal}
                                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                >
                                    <X className="w-6 h-6" />
                                </button>
                            </div>

                            {/* Photo */}
                            <div className="mb-4">
                                <img
                                    src={selectedPhoto.id && photoUrls[selectedPhoto.id] ? photoUrls[selectedPhoto.id] : 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIjZjNmNGY2Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5YWFhYSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkxvYWRpbmcuLi48L3RleHQ+PC9zdmc+'}
                                    alt={selectedPhoto.caption || 'Challenge photo'}
                                    className="w-full rounded-lg"
                                    onLoadStart={() => {
                                        // Trigger loading of full image when modal opens
                                        if (selectedPhoto.id && selectedPhoto.blobName && !photoUrls[selectedPhoto.id]) {
                                            console.log(`[ChallengeWall] Modal opened, triggering full image load for photo ${selectedPhoto.id}`);
                                            createDisplayableImageUrl(selectedPhoto.blobName, [selectedPhoto], false);
                                        }
                                    }}
                                    onError={(e) => {
                                        console.error(`[ChallengeWall] Modal image load error for photo ${selectedPhoto.id || 'unknown'}:`, e);
                                        console.error(`[ChallengeWall] Modal photo URL:`, selectedPhoto.id ? (photoUrls[selectedPhoto.id] || selectedPhoto.photoUrl) : selectedPhoto.photoUrl);

                                        // Simple error handling - just log the error and let the user retry
                                        console.warn(`[ChallengeWall] Modal image failed to load for photo ${selectedPhoto.id}. User can close and reopen the modal to retry.`);
                                    }}
                                />
                            </div>

                            {/* Caption */}
                            {selectedPhoto.caption && (
                                <div className="mb-4">
                                    <p className="text-gray-900 dark:text-white">{selectedPhoto.caption}</p>
                                </div>
                            )}

                            {/* User Info */}
                            <div className="flex items-center justify-between mb-4 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                                <div className="flex items-center space-x-2">
                                    <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                                        <User className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                                    </div>
                                    <div>
                                        <p className="font-medium text-gray-900 dark:text-white">
                                            {selectedPhoto.userDisplayName}
                                        </p>
                                        <p className="text-sm text-gray-500 dark:text-gray-400">
                                            {formatDate(selectedPhoto.postedAt)}
                                        </p>
                                    </div>
                                </div>

                            </div>

                            {/* Comments */}
                            <div className="mb-4">
                                <h4 className="font-medium text-gray-900 dark:text-white mb-3">
                                    Comments ({selectedPhoto.comments?.length || 0})
                                </h4>
                                <div className="space-y-3 max-h-40 overflow-y-auto">
                                    {!selectedPhoto.comments || (selectedPhoto.comments?.length ?? 0) === 0 ? (
                                        <p className="text-gray-500 dark:text-gray-400 text-sm">
                                            No comments yet. Be the first to comment!
                                        </p>
                                    ) : (
                                        (selectedPhoto.comments || []).map((comment) => (
                                            <div key={comment.id} className="flex space-x-3">
                                                <div className="w-8 h-8 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center">
                                                    <User className="w-4 h-4 text-gray-500" />
                                                </div>
                                                <div className="flex-1">
                                                    <div className="flex items-center space-x-2 mb-1">
                                                        <span className="font-medium text-gray-900 dark:text-white text-sm">
                                                            {comment.userDisplayName}
                                                        </span>
                                                        <span className="text-xs text-gray-500 dark:text-gray-400">
                                                            {formatDate(comment.postedAt)}
                                                        </span>
                                                    </div>
                                                    <p className="text-gray-700 dark:text-gray-300 text-sm">
                                                        {comment.comment}
                                                    </p>
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>

                            {/* Add Comment */}
                            <div className="flex space-x-3">
                                <input
                                    type="text"
                                    value={newComment}
                                    onChange={(e) => setNewComment(e.target.value)}
                                    placeholder="Add a comment..."
                                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    onKeyPress={(e) => e.key === 'Enter' && handleAddComment()}
                                />
                                <button
                                    onClick={handleAddComment}
                                    disabled={!newComment.trim() || isPostingComment}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                                >
                                    {isPostingComment ? 'Posting...' : 'Post'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
